/**
 * 用户中心相关接口
 * 对应 Apipost: 用户端 > 用户中心
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 用户详细信息 */
export interface UserProfile {
  uid: number;
  account: string;
  nickname: string;
  avatar: string;
  phone: string; // 脱敏
  real_name: string;
  status: number; // 0禁用 1正常
  status_text: string;
  is_real: number; // 0未实名 1已实名
  is_apead: number; // 是否推广员：0否 1是
  is_apead_text: string;
  is_talent: number; // 是否达人：0否 1是
  spread_count: number; // 推广人数
  add_time: string;
}

/** 推荐人信息 */
export interface ParentUser {
  uid: number;
  nickname: string;
  avatar: string;
  phone: string;
}

/** 推广统计 */
export interface SpreadStats {
  first_level_count: number; // 一级推广人数
  total_count: number; // 总推广人数
}

/** 用户详情响应 */
export interface UserProfileResponse {
  code: number;
  message: string;
  data: {
    user: UserProfile;
    spread_stats: SpreadStats;
    parent_user: ParentUser | null;
    /** 是否达人：0否 1是（data 层，user 层也有同名冗余字段） */
    is_talent?: number;
  };
}

/** 实名认证请求参数 */
export interface VerifyIdentityParams {
  card_id: string; // 身份证号
  phone: string; // 手机号
  real_name: string; // 真实姓名
  nickname?: string;
  avatar?: string;
  birthday?: string;
}

/** 实名认证响应中的用户信息 */
export interface VerifyUserResult {
  uid: number;
  nickname: string;
  avatar: string;
  real_name: string;
}

// ==================== 接口调用 ====================

/**
 * 接口1：获取用户详细信息
 * GET /home/user/profile/detail
 * 返回当前登录用户的详细信息，包括用户信息、推广数据、推荐人信息
 */
export async function fetchUserProfile(): Promise<UserProfileResponse["data"]> {
  console.log("[API] 获取用户详细信息:", "/home/user/profile/detail");

  const res = await http.get<UserProfileResponse>("/home/user/profile/detail");

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取用户信息失败");
  }

  return res.data.data;
}

/**
 * 接口2：用户实名认证
 * POST /home/user/profile/verify-identity
 * 更新当前登录用户的实名信息
 */
export async function verifyIdentity(params: VerifyIdentityParams): Promise<{ message: string; user: VerifyUserResult }> {
  console.log("[API] 实名认证:", "/home/user/profile/verify-identity", params);

  const res = await http.post<{
    code: number;
    message: string;
    data: { message: string; user: VerifyUserResult };
  }>("/home/user/profile/verify-identity", params);

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "实名认证失败");
  }

  return res.data.data;
}

// ==================== 出行人列表接口 ====================

/** 出行人库 - 单条记录 */
export interface UserTravelerItem {
  id: number;
  user_id: number;
  name: string;
  card_type: number;        // 1=身份证
  card_type_text: string;
  card_no: string;          // 身份证号
  phone: string;
  id_card_front?: string | null;
  id_card_back?: string | null;
  role_type: number;        // 1=本人 5=朋友
  role_type_text: string;
  is_default: number;       // 1=默认
  sort: number;
  add_time: number;
  add_time_text: string;
}

/** 出行人列表响应 */
export interface UserTravelerListResponse {
  code: number;
  message: string;
  data: {
    list: UserTravelerItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  };
}

/**
 * 获取用户出行人列表
 * GET /home/user-traveler/list?page={page}&limit={limit}
 * 一进入订单填写页即调用，用于回填常用出行人
 */
export async function fetchUserTravelerList(
  page = 1,
  limit = 20,
): Promise<UserTravelerItem[]> {
  const res = await http.get<UserTravelerListResponse>(
    "/home/user-traveler/list",
    { params: { page, limit } },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取出行人列表失败");
  }

  return res.data.data.list;
}

// ==================== 新增出行人 ====================

/** 新增出行人请求参数 */
export interface CreateUserTravelerParams {
  name: string;              // 姓名，必填，最大50字符
  card_type: string | number; // 证件类型：1=身份证(默认) 2=户口本
  card_no: string;           // 证件号码，必填，最大20字符
  phone?: string;            // 联系电话，最大20字符
  id_card_front?: string;    // 证件正面照片URL，最大255字符
  id_card_back?: string;     // 证件反面照片URL，最大255字符
  role_type?: string | number; // 角色身份：1=本人(默认) 2=子女 3=父母 4=伴侣 5=朋友
  is_default?: string | number; // 是否默认：1=是 0=否（默认）
  sort?: string | number;    // 排序，默认0，小的在前
}

/** 新增出行人返回值 */
export interface CreateUserTravelerResult {
  id: number;                // 新创建的出行人ID
  message: string;           // 接口返回提示信息
}

/**
 * 新增用户出行人
 * POST /home/user-traveler/create (application/x-www-form-urlencoded)
 * 填写出行人表单保存后调用，将出行人保存到个人库
 */
export async function createUserTraveler(
  params: CreateUserTravelerParams,
): Promise<CreateUserTravelerResult> {
  console.log("[API] 新增出行人:", "/home/user-traveler/create", params);

  const res = await http.post<{
    code: number;
    message: string;
    data: { id: number };
  }>(
    "/home/user-traveler/create",
    new URLSearchParams(
      Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => [k, String(v)]),
      ),
    ),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "添加出行人失败");
  }

  return { id: res.data.data.id, message: res.data.message };
}

// ==================== 编辑出行人 ====================

/** 编辑出行人请求参数（继承新增参数，额外要求 id） */
export interface UpdateUserTravelerParams extends CreateUserTravelerParams {
  id: number | string;       // 出行人ID，必填
}

/** 编辑出行人返回值 */
export interface UpdateUserTravelerResult {
  id: number;
  message: string;           // 接口返回提示信息
}

/**
 * 编辑用户出行人
 * POST /home/user-traveler/update (application/x-www-form-urlencoded)
 * 编辑出行人表单保存后调用
 */
export async function updateUserTraveler(
  params: UpdateUserTravelerParams,
): Promise<UpdateUserTravelerResult> {
  console.log("[API] 编辑出行人:", "/home/user-traveler/update", params);

  const res = await http.post<{
    code: number;
    message: string;
    data: { id: number };
  }>(
    "/home/user-traveler/update",
    new URLSearchParams(
      Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== "")
          .map(([k, v]) => [k, String(v)]),
      ),
    ),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "更新出行人失败");
  }

  return { id: res.data.data.id, message: res.data.message };
}

// ==================== 删除出行人 ====================

/**
 * 删除用户出行人
 * POST /home/user-traveler/delete (application/x-www-form-urlencoded)
 */
export async function deleteUserTraveler(
  id: number | string,
): Promise<string> {
  console.log("[API] 删除出行人:", "/home/user-traveler/delete", id);

  const res = await http.post<{
    code: number;
    message: string;
    data: null;
  }>(
    "/home/user-traveler/delete",
    new URLSearchParams({ id: String(id) }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "删除出行人失败");
  }

  return res.data.message;
}

// ==================== 设置默认出行人 ====================

/**
 * 设置默认出行人
 * POST /home/user-traveler/set-default (application/x-www-form-urlencoded)
 */
export async function setDefaultUserTraveler(
  id: number | string,
): Promise<string> {
  console.log("[API] 设置默认出行人:", "/home/user-traveler/set-default", id);

  const res = await http.post<{
    code: number;
    message: string;
    data: { id: number };
  }>(
    "/home/user-traveler/set-default",
    new URLSearchParams({ id: String(id) }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "设置默认失败");
  }

  return res.data.message;
}

/** 出行人详情响应 */
export interface UserTravelerDetailResponse {
  code: number;
  message: string;
  data: UserTravelerItem;
}

/**
 * 获取用户出行人详情
 * GET /home/user-traveler/detail?id={id}
 * 点击出行人时查看完整详情
 */
export async function fetchUserTravelerDetail(
  id: number,
): Promise<UserTravelerItem> {
  const res = await http.get<UserTravelerDetailResponse>(
    "/home/user-traveler/detail",
    { params: { id } },
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取出行人详情失败");
  }

  return res.data.data;
}

// ==================== 他人信息 ====================

/** 他人信息（用于评价记录页头部展示） */
export interface OtherUserData {
  uid: number;
  nickname: string;
  avatar: string;
}

export interface OtherUserInfoResponse {
  code: number;
  message: string;
  data: OtherUserData;
}

/**
 * 获取他人信息
 * GET /home/user/other/info?uid={uid}
 */
export async function getOtherUserInfo(uid: number): Promise<OtherUserData> {
  const res = await http.get<OtherUserInfoResponse>("/home/user/other/info", { params: { uid } });
  if (res.data.code !== 200) throw new Error(res.data.message || "获取用户信息失败");
  return res.data.data;
}
